import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GeneratePanel } from "@/components/GeneratePanel";
import { Clapperboard, Film, Utensils, Play } from "lucide-react";
import heroImg from "@/assets/hero.jpg";

const HERO = heroImg;

const steps = [
  {
    icon: Film,
    title: "Pick a Movie",
    desc: "Type in any film you love, whether it's a cult classic, a rom-com, or a horror flick. Anything goes.",
  },
  {
    icon: Utensils,
    title: "Get a Pairing",
    desc: "Our AI chef whips up a themed meal, a signature cocktail, and a mocktail, with full recipes included.",
  },
  {
    icon: Play,
    title: "Cook & Watch",
    desc: "Set the table, pour the drinks, hit play. Dinner and a movie, elevated.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && user !== null;

  const scrollToGenerate = () => {
    const el = document.getElementById("generate");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={HERO}
          alt="Dim cinema"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 sm:pb-28 animate-fade-up">
          <div className="flex items-center gap-2 mb-5">
            <Clapperboard className="w-5 h-5 text-[#E50914]" />
            <span className="uppercase text-xs tracking-[0.3em] text-white/60">
              Dinner & a Movie, Reimagined
            </span>
          </div>
          <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl leading-[0.95] max-w-4xl">
            Every film deserves
            <br />
            its own <span className="text-[#E50914]">feast</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl">
            Just type in a movie title and we'll put together a dinner pairing made
            for it: a themed meal, a cocktail, and a mocktail, all with full recipes.
            Save your favorites, rate them, and share them.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button
              onClick={scrollToGenerate}
              data-testid={isAuthed ? "hero-generate-btn" : "hero-get-started-btn"}
              className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-12 px-8 text-base"
            >
              Generate a Pairing
            </Button>
            <Button
              onClick={() => navigate("/community")}
              data-testid="hero-community-btn"
              variant="ghost"
              className="rounded-full bg-white/10 hover:bg-white/20 text-white h-12 px-8 text-base"
            >
              Explore Community
            </Button>
          </div>
        </div>
      </section>

      {/* GENERATE */}
      <section
        id="generate"
        className="scroll-mt-20 max-w-4xl mx-auto px-5 sm:px-8 py-24"
        data-testid="home-generate-section"
      >
        <div className="mb-8 text-center">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
            Generate
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-2">
            What are you watching tonight?
          </h2>
          <p className="text-white/50 mt-3 max-w-xl mx-auto">
            Enter a movie title and we'll compose the perfect meal to match.
          </p>
        </div>
        <GeneratePanel showHeader={false} />
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="how-it-works">
        <div className="mb-14">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
            How it works
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3">
            Three steps to showtime.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/5 bg-[#0d0d0d] p-8 hover:border-white/20 transition-colors duration-300"
              data-testid={`step-card-${i}`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 rounded-full bg-[#E50914]/15 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-[#E50914]" />
                </div>
                <span className="font-display text-5xl font-black text-white/10">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl font-bold mb-2">{s.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Button
            onClick={scrollToGenerate}
            data-testid="cta-bottom-btn"
            className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-12 px-10 text-base"
          >
            {isAuthed ? "Generate a Pairing" : "Generate Your First Pairing"}
          </Button>
        </div>
      </section>
    </div>
  );
}
